import * as React from "react"
import { Link, useParams } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"
import { SectionCard } from "@/components/console/SectionCard"
import { MarkdownView } from "@/components/console/MarkdownView"
import { PageSkeleton, FetchError } from "@/components/console/Fallbacks"
import { Badge } from "@/components/ui/badge"
import { timeAgo } from "@/lib/format"

export function Record({ token }: { token: string }) {
  const { audience = "", id = "" } = useParams()
  const [statusError, setStatusError] = React.useState<string | null>(null)

  const record = useQuery({
    queryKey: ["record", audience, id, token],
    queryFn: () => api.record(token, audience, id),
  })
  const audiences = useQuery({
    queryKey: ["audiences", token],
    queryFn: () => api.audiences(token),
  })

  const qc = useQueryClient()
  const toggleStatus = useMutation({
    mutationFn: () =>
      api.editRecord(
        token,
        audience,
        id,
        { status: record.data!.record.status === "active" ? "retired" : "active" },
        record.data!.etag
      ),
    onSuccess: () => {
      setStatusError(null)
      qc.invalidateQueries({ queryKey: ["record", audience, id] })
    },
    onError: (error: Error) => {
      setStatusError(error.message)
    },
  })

  if (record.isPending || audiences.isPending) return <PageSkeleton />
  if (record.isError) return <FetchError error={record.error} retry={() => record.refetch()} />
  if (audiences.isError) return <FetchError error={audiences.error} retry={() => audiences.refetch()} />

  const canWrite = audiences.data.find((a) => a.id === audience)?.can_write ?? false
  const { record: r, note, collection } = record.data

  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_16rem]">
      <SectionCard title={r.title}>
        <MarkdownView content={r.content} audience={audience} />
      </SectionCard>
      <SectionCard title="metadata">
        <dl className="space-y-2 font-mono text-xs">
          <div>
            <dt className="text-muted-foreground">status</dt>
            <dd>
              <Badge>{r.status}</Badge>
            </dd>
          </div>
          {collection ? (
            // An entity record is a COLLECTION, not a claim (ADR 0024): it has no
            // trust bucket because there is no single statement to attest to. Say
            // what it is for, so nobody reads it as vetted knowledge.
            <div>
              <dt className="text-muted-foreground">kind</dt>
              <dd className="space-x-1">
                <Badge variant="outline">collection</Badge>
                <span className="text-muted-foreground">orients, not evidence</span>
              </dd>
            </div>
          ) : null}
          {note ? (
            // A personal record is the viewer's own note, not a curated fact with
            // a trust bucket (ADR 0021). Freshness is still shown honestly.
            <div>
              <dt className="text-muted-foreground">kept</dt>
              <dd className="space-x-1">
                <Badge variant="outline">your note</Badge>
                {note.unverified ? <Badge variant="secondary">stale</Badge> : null}
              </dd>
            </div>
          ) : null}
          <div>
            <dt className="text-muted-foreground">tags</dt>
            <dd>{r.tags.length > 0 ? r.tags.join(", ") : "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">version</dt>
            <dd>{r.version}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">updated</dt>
            <dd>
              {timeAgo(r.updated_at)} by {r.updated_by}
            </dd>
          </div>
          {r.source_refs.length > 0 ? (
            <div>
              <dt className="text-muted-foreground">sources</dt>
              <dd className="space-y-1">
                {r.source_refs.map((s) => (
                  <a
                    key={s.uri}
                    href={s.uri}
                    className="block truncate text-primary hover:underline"
                  >
                    {s.label ?? s.uri}
                  </a>
                ))}
              </dd>
            </div>
          ) : null}
        </dl>
        {canWrite ? (
          <div className="mt-3 space-y-1 border-t border-border pt-3">
            <Link
              to={`/records/${audience}/${id}/edit`}
              className="block font-mono text-xs text-primary hover:underline"
            >
              edit
            </Link>
            <Link
              to={`/records/${audience}/${id}/history`}
              className="block font-mono text-xs text-primary hover:underline"
            >
              history
            </Link>
            {statusError ? (
              <p className="font-mono text-xs" style={{ color: "var(--viz-failed)" }}>
                {statusError}
              </p>
            ) : null}
            <button
              onClick={() => toggleStatus.mutate()}
              disabled={toggleStatus.isPending}
              className="block font-mono text-xs text-primary hover:underline"
            >
              {r.status === "active" ? "retire" : "restore"}
            </button>
          </div>
        ) : null}
      </SectionCard>
    </div>
  )
}
